import { useEffect, useMemo, useState } from 'react';
import type { Session } from '@tla/types';
import { api } from '../api/client';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useLayoutStore } from '../store/layoutStore';
import { formatTimestamp, formatLapTime } from '../util/format';
import { LapTable } from './LapTable';
import { CollapseHeader } from './CollapseHeader';
import { EventStats } from './EventStats';

/**
 * Left column: event list → sessions for the selected event (grouped by driver)
 * → expandable lap table per session.
 */
export function EventBrowser(): React.ReactElement {
  const store = useWorkspaceStore();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Team names that are currently collapsed in the sidebar.
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());
  // Stats modal: null = closed, { team } = team-scoped, {} = whole event.
  const [stats, setStats] = useState<{ team?: string } | null>(null);
  const eventsCollapsed = useLayoutStore((s) => !!s.collapsed.events);
  const sessionsCollapsed = useLayoutStore((s) => !!s.collapsed.sessions);

  const selectedEventId = store.selectedEventId;
  const ensureSummary = store.ensureSummary;

  // Eagerly load each listed session's summary so the stint heading can show
  // the driver's best lap without waiting for the user to expand the stint.
  useEffect(() => {
    for (const s of sessions) void ensureSummary(s.id);
  }, [sessions, ensureSummary]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId) {
      setSessions([]);
      return;
    }
    void api.listSessions(selectedEventId).then((rows) => {
      if (!cancelled) setSessions(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, store.events]);

  const grouped = useMemo(() => {
    // Group by team first (to separate teams in the list), then by driver.
    // Team lives on the session (a per-entry attribute), not the car model.
    const byTeam = new Map<string, Map<string, Session[]>>();
    for (const s of sessions) {
      const team = s.team ?? '';
      const byDriver = byTeam.get(team) ?? new Map<string, Session[]>();
      const list = byDriver.get(s.driverId) ?? [];
      list.push(s);
      byDriver.set(s.driverId, list);
      byTeam.set(team, byDriver);
    }
    return [...byTeam.entries()].map(([team, drivers]) => ({
      team,
      drivers: [...drivers.entries()],
    }));
  }, [sessions]);

  async function removeSession(id: string): Promise<void> {
    if (!confirm('Delete this session and its telemetry?')) return;
    await store.removeSession(id);
    setSessions((rows) => rows.filter((r) => r.id !== id));
  }

  function toggleTeam(team: string): void {
    setCollapsedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(team)) next.delete(team);
      else next.add(team);
      return next;
    });
  }

  return (
    <div className="col-left">
      <CollapseHeader title="Events" k="events" />
      {!eventsCollapsed && (
        <ul className="list">
          {store.eventOrder.length === 0 && (
            <li className="list-item muted small">No events yet — import a session.</li>
          )}
          {store.eventOrder.map((id) => {
            const ev = store.events[id];
            const track = store.tracks[ev.trackId];
            return (
              <li
                key={id}
                className={`list-item ${selectedEventId === id ? 'active' : ''}`}
                onClick={() => void store.selectEvent(id)}
              >
                <div>{ev.name}</div>
                <div className="small muted">
                  {ev.date} · {track?.name ?? 'Unknown track'}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {selectedEventId && (
        <>
          <div className="sessions-head-row">
            <CollapseHeader title="Sessions" k="sessions" />
            {!sessionsCollapsed && (
              <button
                className="small"
                onClick={() => setStats({})}
                style={{ flexShrink: 0, marginRight: 12 }}
              >
                Event stats
              </button>
            )}
          </div>
          {!sessionsCollapsed && (
            <>
              {grouped.length === 0 && (
                <div className="list-item muted small">No sessions in this event.</div>
              )}
              {grouped.map(({ team, drivers }) => (
                <div key={team || '__noteam__'}>
                  {team && (
                    <div
                      className="small team-head"
                      onClick={() => toggleTeam(team)}
                      style={{
                        padding: '4px 12px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            transform: collapsedTeams.has(team) ? 'rotate(-90deg)' : 'none',
                            transition: 'transform 0.15s',
                          }}
                        >
                          ▾
                        </span>
                        {team}
                      </span>
                      <button
                        className="small"
                        title={`Stats for ${team}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStats({ team });
                        }}
                      >
                        Team stats
                      </button>
                    </div>
                  )}
                  {!(team && collapsedTeams.has(team)) &&
                    drivers.map(([driverId, list]) => (
                    <div key={driverId}>
                      <div className="small" style={{ padding: '4px 12px', color: 'var(--accent)' }}>
                        {store.drivers[driverId]?.name ?? 'Unknown driver'}
                      </div>
                      {list.map((s) => {
                        const carEntry = store.cars[s.carId];
                        const carLabel = carEntry
                          ? `${s.carNumber ? `#${s.carNumber} · ` : ''}${carEntry.name}`
                          : '—';
                        const summary = store.summaries[s.id];
                        const bestLapTime =
                          summary && summary.bestLapNumber != null
                            ? summary.laps.find((l) => l.number === summary.bestLapNumber)?.lapTime
                            : undefined;
                        return (
                          <div key={s.id}>
                            <div
                              className="list-item"
                              onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Stint {s.stint}</span>
                                <button
                                  className="danger small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void removeSession(s.id);
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="small muted">
                                {carLabel} · {formatTimestamp(s.createdTs)}
                                {bestLapTime != null && (
                                  <>
                                    {' · '}
                                    <span className="stint-best">{formatLapTime(bestLapTime)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {expanded === s.id && <LapTable sessionId={s.id} />}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </>
      )}
      {stats && (
        <EventStats
          onClose={() => setStats(null)}
          {...(stats.team ? { teamFilter: stats.team } : {})}
        />
      )}
    </div>
  );
}
